import React, { useState } from 'react';
import { useParticipante } from '../../../contexts/ParticipanteContext';
import { ShareModal } from '../../../components/social/ShareModal';
import { CelebrationModal } from '../../../components/social/CelebrationModal';

export default function NiveisPage() {
  const { participante, campanhaAtual } = useParticipante();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState<any>(null);
  
  // Usar a campanha atual do contexto (selecionada no header)
  const campanhaSelecionada = campanhaAtual;
  const progressao = campanhaSelecionada?.progressao;

  // Definição dos níveis
  const niveis = [
    {
      id: 1,
      nome: 'Bronze',
      icone: '🥉',
      xpMinimo: 0,
      xpMaximo: 999,
      cor: 'from-amber-700 to-amber-900',
      corBorda: 'border-amber-700',
      corBg: 'bg-amber-50',
      beneficios: [
        'Acesso ao catálogo básico de recompensas',
        'Participação em campanhas',
        'Suporte por email'
      ]
    },
    {
      id: 2,
      nome: 'Prata',
      icone: '🥈',
      xpMinimo: 1000,
      xpMaximo: 4999,
      cor: 'from-gray-400 to-gray-600',
      corBorda: 'border-gray-400',
      corBg: 'bg-gray-50',
      beneficios: [
        'Todos os benefícios do Bronze',
        '+10% de bônus em XP',
        'Acesso a recompensas exclusivas',
        'Prioridade em sorteios'
      ]
    },
    {
      id: 3,
      nome: 'Ouro',
      icone: '🥇',
      xpMinimo: 5000,
      xpMaximo: 14999,
      cor: 'from-yellow-400 to-yellow-600',
      corBorda: 'border-yellow-400',
      corBg: 'bg-yellow-50',
      beneficios: [
        'Todos os benefícios da Prata',
        '+20% de bônus em XP',
        'Desconto em recompensas premium',
        'Suporte prioritário',
        'Acesso antecipado a novos jogos'
      ]
    },
    {
      id: 4,
      nome: 'Platina',
      icone: '💎',
      xpMinimo: 15000,
      xpMaximo: 49999,
      cor: 'from-cyan-400 to-blue-600',
      corBorda: 'border-cyan-400',
      corBg: 'bg-cyan-50',
      beneficios: [
        'Todos os benefícios do Ouro',
        '+30% de bônus em XP',
        'Recompensas VIP exclusivas',
        'Gerente de conta dedicado',
        'Eventos exclusivos',
        'Multiplicador de pontos em datas especiais'
      ]
    },
    {
      id: 5,
      nome: 'Diamante',
      icone: '💠',
      xpMinimo: 50000,
      xpMaximo: 999999,
      cor: 'from-purple-400 to-pink-600',
      corBorda: 'border-purple-400',
      corBg: 'bg-purple-50',
      beneficios: [
        'Todos os benefícios da Platina',
        '+50% de bônus em XP',
        'Catálogo premium ilimitado',
        'Concierge personalizado',
        'Viagens e experiências exclusivas',
        'Participação em eventos corporativos',
        'Reconhecimento público nas campanhas'
      ]
    }
  ];

  // Função para obter o nível atual baseado no XP
  const getNivelAtual = (xp: number) => {
    return niveis.find(n => xp >= n.xpMinimo && xp <= n.xpMaximo) || niveis[0];
  };

  // Função para obter o próximo nível
  const getProximoNivel = (xp: number) => {
    const nivelAtual = getNivelAtual(xp);
    return niveis.find(n => n.id === nivelAtual.id + 1);
  };

  // Função para calcular o bônus de XP
  const getBonusXP = (nivelId: number) => {
    const bonus = [0, 10, 20, 30, 50];
    return bonus[nivelId - 1] || 0;
  };

  if (!campanhaAtual || !campanhaAtual.recursos?.niveis) {
    return (
      <div className="space-y-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-trophy-line text-4xl text-gray-400"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {!campanhaAtual ? 'Nenhuma Campanha Selecionada' : 'Campanha sem Sistema de Níveis'}
          </h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {!campanhaAtual 
              ? 'Selecione uma campanha no header para visualizar os níveis e progressão.'
              : 'A campanha selecionada não possui o sistema de níveis e progressão ativado.'
            }
          </p>
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/premiado/campanhas')}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-red-600 transition-all whitespace-nowrap cursor-pointer"
          >
            Ver Campanhas Disponíveis
          </button>
        </div>
      </div>
    );
  }

  // Usar dados da massa de teste da campanha selecionada
  const xpAtual = progressao?.xpAtual || 0;
  const nivelAtual = getNivelAtual(xpAtual);
  const proximoNivel = getProximoNivel(xpAtual);
  const xpProximoNivel = proximoNivel?.xpMinimo || nivelAtual.xpMaximo;
  const xpFaltante = xpProximoNivel - xpAtual;
  const progressoPercentual = proximoNivel 
    ? ((xpAtual - nivelAtual.xpMinimo) / (proximoNivel.xpMinimo - nivelAtual.xpMinimo)) * 100
    : 100;

  const handleShareLevel = () => {
    if (nivelAtual) {
      setSelectedAchievement({
        title: `Nível ${nivelAtual.nome} Alcançado!`,
        description: `Alcancei o nível ${nivelAtual.nome} com ${progressao?.xpAtual.toLocaleString()} XP na campanha ${campanhaAtual?.nome}!`,
        type: 'level' as const
      });
      setShareModalOpen(true);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Níveis e Progressão
          </h1>
          <p className="text-gray-600">
            Acompanhe sua evolução na campanha <strong>{campanhaSelecionada.nome}</strong>
          </p>
        </div>
      </div>

      {/* Info da Campanha Selecionada */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
            <i className="ri-trophy-line text-2xl text-orange-500"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">
              {campanhaSelecionada.nome}
            </h3>
            <p className="text-sm text-gray-600">
              {campanhaSelecionada.descricao}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-orange-600">
              {(progressao?.pontosDisponiveis || 0).toLocaleString('pt-BR')}
            </div>
            <div className="text-sm text-gray-600">pontos disponíveis</div>
          </div>
        </div>
      </div>

      {/* Card do Nível Atual */}
      <div className={`bg-gradient-to-br ${nivelAtual.cor} rounded-2xl p-8 text-white shadow-xl`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="text-6xl">{nivelAtual.icone}</div>
            <div>
              <div className="text-sm opacity-90 mb-1">Nível Atual</div>
              <div className="text-3xl font-bold">{nivelAtual.nome}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{xpAtual.toLocaleString('pt-BR')}</div>
            <div className="text-sm opacity-90">XP Total</div>
          </div>
        </div>

        {/* Barra de Progresso */}
        {proximoNivel && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progresso para {proximoNivel.nome}</span>
              <span>{Math.round(progressoPercentual)}%</span>
            </div>
            <div className="h-3 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${progressoPercentual}%` }}
              />
            </div>
            <div className="text-sm opacity-90">
              Faltam {xpFaltante.toLocaleString('pt-BR')} XP para o próximo nível
            </div>
          </div>
        )}

        {/* Estatísticas */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/20">
          <div>
            <div className="text-2xl font-bold">{Math.round(progressoPercentual)}%</div>
            <div className="text-sm opacity-90">Progresso</div>
          </div>
          <div>
            <div className="text-2xl font-bold">+{getBonusXP(nivelAtual.id)}%</div>
            <div className="text-sm opacity-90">Bônus XP</div>
          </div>
          <div>
            <div className="text-2xl font-bold">#{progressao?.ranking || 0}</div>
            <div className="text-sm opacity-90">Posição no Ranking</div>
          </div>
        </div>
      </div>

      {/* Jornada de Níveis */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Jornada de Níveis</h2>
        
        <div className="relative">
          {/* Linha de Progresso */}
          <div className="absolute left-8 top-0 bottom-0 w-1 bg-gray-200" />
          <div 
            className="absolute left-8 top-0 w-1 bg-gradient-to-b from-orange-500 to-red-500 transition-all duration-500"
            style={{ height: `${((nivelAtual.id - 1) / (niveis.length - 1)) * 100}%` }}
          />

          {/* Níveis */}
          <div className="space-y-6">
            {niveis.map((nivel) => {
              const isAtual = nivel.id === nivelAtual.id;
              const isConquistado = nivel.id < nivelAtual.id;
              const isBloqueado = nivel.id > nivelAtual.id;

              return (
                <div key={nivel.id} className="relative flex items-start gap-6">
                  {/* Ícone do Nível */}
                  <div className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center text-3xl ${
                    isAtual ? `bg-gradient-to-br ${nivel.cor} shadow-lg` :
                    isConquistado ? 'bg-green-500' :
                    'bg-gray-200'
                  }`}>
                    {isConquistado ? '✓' : nivel.icone}
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 pb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{nivel.nome}</h3>
                      {isAtual && (
                        <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full whitespace-nowrap">
                          Nível Atual
                        </span>
                      )}
                      {isConquistado && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap">
                          Conquistado
                        </span>
                      )}
                      {isBloqueado && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full whitespace-nowrap">
                          Bloqueado
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      {nivel.xpMinimo.toLocaleString('pt-BR')} - {nivel.xpMaximo.toLocaleString('pt-BR')} XP
                    </div>
                    {isAtual && (
                      <div className="mb-3">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-gradient-to-r ${nivel.cor} transition-all duration-500`}
                            style={{ width: `${progressoPercentual}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cards de Benefícios */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {niveis.map((nivel) => {
          const isAtual = nivel.id === nivelAtual.id;
          const isConquistado = nivel.id < nivelAtual.id;

          return (
            <div 
              key={nivel.id}
              className={`rounded-xl border-2 p-6 transition-all ${
                isAtual 
                  ? `${nivel.corBorda} ${nivel.corBg} shadow-lg` 
                  : isConquistado
                  ? 'border-green-200 bg-green-50'
                  : 'border-gray-200 bg-white opacity-60'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="text-3xl">{nivel.icone}</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{nivel.nome}</h3>
                  <div className="text-sm text-gray-600">
                    {nivel.xpMinimo.toLocaleString('pt-BR')}+ XP
                  </div>
                </div>
                {nivel.id > 1 && (
                  <div className="ml-auto px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full whitespace-nowrap">
                    +{getBonusXP(nivel.id)}% XP
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {nivel.beneficios.map((beneficio, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-gray-700">{beneficio}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dica para Evoluir */}
      {proximoNivel && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-orange-500"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-2">
                Dica para alcançar o nível {proximoNivel.nome}
              </h3>
              <p className="text-gray-700 mb-3">
                Complete missões, participe de jogos e atinja seus objetivos para ganhar mais XP. 
                Você está a apenas {xpFaltante.toLocaleString('pt-BR')} XP de desbloquear benefícios exclusivos!
              </p>
              <button
                onClick={handleShareLevel}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-share-line"></i>
                Compartilhar Progresso
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Compartilhamento */}
      {selectedAchievement && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          achievement={selectedAchievement}
        />
      )}
    </div>
  );
}
